import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { CodeRequestType, PartialBookingUserWithToken } from 'src/services/auth'
import { queryStringOrSessionStorage } from 'src/utils/queryStringOrSessionStorage'

type VerificationResponse = Omit<PartialBookingUserWithToken, "tokenResponse">;
type PaymentStatus =  "NOT_STARTED" |   "STARTED" |  "COMPLETED";

export type BookingFlowState = {
  steps: {
    current: number,
    max: number
  }
  verification?: VerificationResponse;
  codeRequestType?: CodeRequestType;
  patientName: string;
  patientDOB: string;
  sawAdditionalDetailsScreen: boolean;
  sawPrepaymentMessage: boolean;
  paymentStatus: PaymentStatus
}

const storedVerificationResponse = Object.keys(sessionStorage).reduce((createBookingResponse, key) => {
  const [a,b] = key.split('.')
  if(a === "reservedBooking" && b) {
    createBookingResponse[b as keyof VerificationResponse] = JSON.parse(sessionStorage.getItem(key)!);
  }
  return createBookingResponse
}, {} as Partial<VerificationResponse>);

const initialState: BookingFlowState = {
  steps: {
    current: 0,
    max: 5
  },
  verification: ("mobile" in  storedVerificationResponse) ? storedVerificationResponse as VerificationResponse : undefined,
  patientName: queryStringOrSessionStorage("patientName")!,
  patientDOB:  queryStringOrSessionStorage("patientDOB")!,
  codeRequestType: (sessionStorage.getItem("codeRequestType") as CodeRequestType) ?? undefined,
  sawAdditionalDetailsScreen: false,
  sawPrepaymentMessage: false,
  paymentStatus: "NOT_STARTED",
}

export const bookingFlowSlice = createSlice({
  name: 'bookingFlow',
  initialState: initialState,
  reducers: {
    setStep: (state, action: PayloadAction<number>) => {
        state.steps.current = action.payload;
    },
    setVerificationInfo: (state, action: PayloadAction<VerificationResponse>) => {
      state.verification = action.payload;
      for(const [key,value] of Object.entries(action.payload)) {
        sessionStorage.setItem(`verification.${key}`, JSON.stringify(value));
      }
    },
    setPatientName: (state, action: PayloadAction<string>) => {
        state.patientName = action.payload;
        sessionStorage.setItem("patientName", action.payload)
    },
    setPatientDob: (state, action: PayloadAction<string>) => {
        state.patientDOB = action.payload;
        sessionStorage.setItem("patientDOB", action.payload)
    },
    setCodeRequestType: (state, action: PayloadAction<CodeRequestType>) => {
      state.codeRequestType = action.payload;
      sessionStorage.setItem("codeRequestType", action.payload)
    },
    setSawAdditionalDetailsScreen: (state, action: PayloadAction<boolean>) => {
     state.sawAdditionalDetailsScreen = action.payload; 
    },
    setSawPrepaymentMessage: (state, action: PayloadAction<boolean>) => {
     state.sawPrepaymentMessage = action.payload; 
    },
    setPaymentStatus: (state, action: PayloadAction<PaymentStatus>) => {
      state.paymentStatus = action.payload;
    }
  },
})

// Action creators are generated for each case reducer function
export const { setStep, setVerificationInfo, setPatientDob, setPatientName, setCodeRequestType, setSawAdditionalDetailsScreen, setSawPrepaymentMessage, setPaymentStatus } = bookingFlowSlice.actions

export default bookingFlowSlice.reducer